// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import GameControls from './components/GameControls';
import WinningPatternDisplay from './components/WinningPatternDisplay';
import RecentBallsBar from './components/RecentBallsBar';
import WinnerOverlay from './components/WinnerOverlay';
import SpecialBallOverlay from './components/SpecialBallOverlay';
import RoundSelection from './components/RoundSelection';
import NewGameOptionsModal from './components/NewGameOptionsModal';
import SocialActionsCard from './components/SocialActionsCard';
import BreakModal from './components/BreakModal';
import CalledBallsModal from './components/CalledBallsModal'; // Ensure this is imported
import { getShuffledBalls } from './bingoLogic';
import './App.css';

const BREAK_IMAGE_SRC = "/break.webp"; // REPLACE THIS

function App() {
  const [allBalls, setAllBalls] = useState([]);
  const [drawnBalls, setDrawnBalls] = useState(new Set()); // This Set is passed to CalledBallsModal
  const [drawnBallsHistory, setDrawnBallsHistory] = useState([]);
  const [currentBall, setCurrentBall] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [showWinnerOverlay, setShowWinnerOverlay] = useState(false);
  const [showSpecialBallOverlay, setShowSpecialBallOverlay] = useState(false);
  const [showRoundSelection, setShowRoundSelection] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  const [showNewGameOptions, setShowNewGameOptions] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [showCalledBallsModal, setShowCalledBallsModal] = useState(false); // State for this modal

  const resetGameVariables = useCallback((keepBalls = false) => {
    if (!keepBalls) {
      const shuffledBalls = getShuffledBalls();
      setAllBalls(shuffledBalls);
      setDrawnBalls(new Set()); // Reset the Set of drawn balls
      setDrawnBallsHistory([]);
      setCurrentBall(null);
    }
    setGameOver(false);
    setShowWinnerOverlay(false); setShowSpecialBallOverlay(false);
  }, []);

  const goHomeAndReset = useCallback(() => {
    resetGameVariables(false); setGameStarted(false); setSelectedRound(null);
    setShowRoundSelection(false); setShowNewGameOptions(false);
    setShowBreakModal(false); setShowCalledBallsModal(false); // Close all modals
  }, [resetGameVariables]);

  useEffect(() => { goHomeAndReset(); }, [goHomeAndReset]);

  const handleShowRoundSelectionForNewGame = useCallback(() => {
    resetGameVariables(false); setSelectedRound(null); setGameStarted(false);
    setRoundSelectionMode('newGameReset');
    setShowRoundSelection(true); setShowNewGameOptions(false);
    setShowBreakModal(false); setShowCalledBallsModal(false);
  }, [resetGameVariables]);

  const handleShowRoundSelectionForChangeOnly = useCallback(() => {
    setRoundSelectionMode('changeRoundKeepBalls');
    setShowRoundSelection(true);
    setShowNewGameOptions(false); setShowBreakModal(false); setShowCalledBallsModal(false);
  }, []);
  const [roundSelectionMode, setRoundSelectionMode] = useState('newGameReset');

  const handleRoundSelected = useCallback((round) => {
    setSelectedRound(round); setShowRoundSelection(false);
    if (roundSelectionMode === 'newGameReset') {
      resetGameVariables(false);
    }
    setGameStarted(true);
  }, [roundSelectionMode, resetGameVariables]);

  const handleCancelRoundSelection = useCallback(() => {
    setShowRoundSelection(false);
    if (!selectedRound && !gameStarted) { goHomeAndReset(); }
  }, [selectedRound, gameStarted, goHomeAndReset]);

  const handleShowNewGameOptions = useCallback(() => {
    setShowNewGameOptions(true);
    setShowBreakModal(false); setShowCalledBallsModal(false);
  }, []);

  const handleRestartCurrentRound = useCallback(() => {
    setShowNewGameOptions(false);
    if (selectedRound) { resetGameVariables(false); setGameStarted(true); }
    else { goHomeAndReset(); }
  }, [selectedRound, resetGameVariables, goHomeAndReset]);

  const toggleBreakModal = useCallback(() => {
    setShowBreakModal(prev => !prev);
    if (!showBreakModal) {
        setShowWinnerOverlay(false); setShowSpecialBallOverlay(false);
        setShowRoundSelection(false); setShowNewGameOptions(false); setShowCalledBallsModal(false);
    }
  }, [showBreakModal]);

  const toggleCalledBallsModal = useCallback(() => { // Function to toggle the modal
    setShowCalledBallsModal(prev => !prev);
    if (!showCalledBallsModal) { // If opening this modal
        setShowWinnerOverlay(false); setShowSpecialBallOverlay(false);
        setShowRoundSelection(false); setShowNewGameOptions(false); setShowBreakModal(false);
    }
  }, [showCalledBallsModal]);

  const drawNextBall = useCallback(() => {
    if (!gameStarted || gameOver || showWinnerOverlay || showSpecialBallOverlay || showNewGameOptions || showBreakModal || showCalledBallsModal) return;
    if (allBalls.length > 0) {
      const nextBall = allBalls[0];
      const remainingBalls = allBalls.slice(1);
      const newDrawnBallsSet = new Set(drawnBalls).add(nextBall); // Update the Set
      const newHistory = [nextBall, ...drawnBallsHistory];
      setAllBalls(remainingBalls); setCurrentBall(nextBall);
      setDrawnBalls(newDrawnBallsSet); // Set the updated Set
      setDrawnBallsHistory(newHistory);
      if (nextBall === 69) { setShowSpecialBallOverlay(true); }
      if (remainingBalls.length === 0) { setGameOver(true); }
    } else { setGameOver(true); }
  }, [allBalls, drawnBalls, drawnBallsHistory, gameOver, gameStarted, showWinnerOverlay, showSpecialBallOverlay, showNewGameOptions, showBreakModal, showCalledBallsModal]);

  const declareWinner = useCallback(() => {
    if (gameStarted && !showBreakModal && !showCalledBallsModal) { setShowWinnerOverlay(true); }
  }, [gameStarted, showBreakModal, showCalledBallsModal]);

  const hideWinner = useCallback(() => { setShowWinnerOverlay(false); }, []);
  const hideSpecialBall = useCallback(() => { setShowSpecialBallOverlay(false); }, []);

  const appClasses = `App large-display-mode ${gameStarted ? 'game-active' : ''}`;

  return (
    <div className={appClasses}>
      <header className="App-header">
        <div className="header-left-text">Bailey Craven</div>
        <h1>
          <span className="header-main-title">Powered By: CravenIT Solutions</span>
          <span className='desktop-only header-separator'> | </span>
          <br className='mobile-only'></br>
          <em className="header-social-handle">
            <i className="fab fa-instagram"></i>
            &nbsp;<span>Ballz_deep_at_bradysSRQ</span>
          </em>
        </h1>
        <p className="header-subtitle">The Official Dirty Bingo Game of Brady's</p>
      </header>

      {!gameStarted && !showBreakModal && !showCalledBallsModal && (
        <section className="event-subtitle-banner">
          <h2>THURsty THURsday - Wet Your Whistle!</h2>
        </section>
      )}

      <main className={`game-area ${!gameStarted ? 'initial-view-dashboard' : 'revised-layout'}`}>
        {!gameStarted ? (
          <>
            <button onClick={toggleBreakModal} className="control-button floating-take-a-break-button">
              <i className="fas fa-pause-circle"></i> Take A Break
            </button>
            <div className="initial-screen-content-wrapper">
              <div className="initial-cards-row">
                <div className="info-card start-game-card">
                  <div className="initial-logo-container-app">
                    <img src="/logo.png" alt="Brady's Balls Bingo Logo" className="initial-logo-app" />
                  </div>
                  <button
                    onClick={handleShowRoundSelectionForNewGame}
                    className="control-button start-game-initial-button-app"
                  >
                    Start Bingo Game
                  </button>
                  <p className="initial-instructions-app">Click to choose your round and let the BALLZ drop!</p>
                </div>
                <SocialActionsCard />
              </div>
              <div className="cha-cha-shoutout">
                <span className="cha-cha-intro">with...</span><br />
                <span className="cha-cha-name">CHA-CHA MC-Flirty!</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <GameControls
              currentBall={currentBall}
              onDrawNext={drawNextBall}
              onNewGame={handleShowNewGameOptions}
              remainingCount={allBalls.length}
              isGameOver={gameOver}
              onDeclareWinner={declareWinner}
              selectedRoundName={selectedRound ? selectedRound.name : null}
              onTakeBreak={toggleBreakModal}
              onShowCalledBalls={toggleCalledBallsModal} // Pass toggle function
            />
            <div className="right-panel-active-game">
              {selectedRound && (
                  <WinningPatternDisplay
                      selectedRound={selectedRound}
                      className="winning-patterns-container-themed"
                  />
              )}
              <RecentBallsBar drawnBallsHistory={drawnBallsHistory} />
            </div>
          </>
        )}
      </main>

       {showWinnerOverlay && <WinnerOverlay onClose={hideWinner} />}
       {showSpecialBallOverlay && <SpecialBallOverlay onClose={hideSpecialBall} />}
       {showRoundSelection && (
         <RoundSelection
           onRoundSelect={handleRoundSelected}
           onCancel={handleCancelRoundSelection}
         />
       )}
       {showNewGameOptions && (
         <NewGameOptionsModal
            onRestartRound={handleRestartCurrentRound}
            onChangeRoundKeepBalls={handleShowRoundSelectionForChangeOnly}
            onSelectNewRound={handleShowRoundSelectionForNewGame}
            onGoHome={goHomeAndReset}
            onClose={() => setShowNewGameOptions(false)}
         />
       )}
       {showBreakModal && <BreakModal onClose={toggleBreakModal} imageSrc={BREAK_IMAGE_SRC} />}
       {showCalledBallsModal && (
         <CalledBallsModal
           drawnNumbers={drawnBalls} // Pass the Set of drawn numbers
           onClose={toggleCalledBallsModal}
         />
       )}
    </div>
  );
}

export default App;
